import { Injectable, Type } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ModalConfig {
  requestId?: string; // Optional, used for request-specific modals
  isEditMode?: boolean; // Optional, used to indicate edit mode
  id: string;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closable?: boolean;
  backdrop?: string | boolean;
  hideborder?: boolean;
  component?: Type<any>;
  link?: string;
  data?: any;
}

export interface ConfirmationConfig {
  title?: string;
  message: string;
  infoText?: string; // Optional additional information text
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info' | 'primary';
  requireNote?: boolean;
  noteLabel?: string; // Optional override for the note field's label (defaults to 'NOTES')
  notePlaceholder?: string; // Optional placeholder for the note textarea
  requireAttachment?: boolean; // Optional attachment field
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private readonly modals = new BehaviorSubject<ModalConfig[]>([]);
  public modals$ = this.modals.asObservable();

  openModal(config: ModalConfig): void {
    const currentModals = this.modals.value;
    const existingModal = currentModals.find(modal => modal.id === config.id);

    if (!existingModal) {
      this.modals.next([...currentModals, config]);

      // Prevent body scroll only if this is the first modal
      if (currentModals.length === 0) {
        document.body.style.overflow = 'hidden';
      }
    } else {
      console.warn(`[ModalService] Modal with ID: ${config.id} is already open.`);
    }
  }
  
  closeModal(id: string): void {
    const currentModals = this.modals.value;
    const updatedModals = currentModals.filter(modal => modal.id !== id);
    this.modals.next(updatedModals);

    // Restore body scroll only if no modals are open
    if (updatedModals.length === 0) {
      document.body.style.overflow = 'auto';
    }
  }

  isModalOpen(id: string): boolean {
    return this.modals.value.some(modal => modal.id === id);
  }

  getModalData(id: string): any {
    const modal = this.modals.value.find(modal => modal.id === id);
    return modal?.data;
  }

  handleBackdropClick(modalId: string): void {
    const modalConfig = this.modals.value.find(modal => modal.id === modalId);
    if (modalConfig && modalConfig.backdrop === 'static') {
      // If backdrop is 'static', do nothing when backdrop is clicked
      return;
    }
    // Otherwise, close the modal
    this.closeModal(modalId);
  }

  /**
   * Sets the title of an active modal
   * @param id - The ID of the modal to update
   * @param title - The new title for the modal
   */
  setModalTitle(id: string, title: string): void {
    const currentModals = this.modals.value;
    const modalIndex = currentModals.findIndex(modal => modal.id === id);

    if (modalIndex !== -1) {
      const updatedModals = [...currentModals];
      updatedModals[modalIndex] = { ...updatedModals[modalIndex], title };
      this.modals.next(updatedModals);
    } else {
    }
  }

  openConfirmationDialog(
    confirmationConfig: ConfirmationConfig,
    onConfirm: (note: string, attachment?: File | null) => void,
    onCancel?: () => void
  ): void {
    const modalConfig: ModalConfig = {
      id: 'confirmation-dialog',
      title: confirmationConfig.title || 'Confirm Action',
      size: 'sm',
      closable: true,
      backdrop: 'static',
      data: {
        ...confirmationConfig,
        onConfirm: (note: string, attachment?: File | null) => {
          onConfirm(note, attachment);
          this.closeModal('confirmation-dialog');
        },
        onCancel: () => {
          if (onCancel) onCancel();
          this.closeModal('confirmation-dialog');
        }
      }
    };

    this.openModal(modalConfig);
  }
}